import {Component, OnInit, OnDestroy, inject} from '@angular/core';
import {StepperModule} from "primeng/stepper";
import {InputSwitchModule} from "primeng/inputswitch";
import {NgIf, UpperCasePipe} from "@angular/common";
import {FormsModule} from "@angular/forms";
import {DividerModule} from "primeng/divider";
import {StepsModule} from "primeng/steps";
import {MenuItem} from "primeng/api";
import {SubscriptionsService} from "../services/subscriptions.service";
import {ActivatedRoute, Router} from "@angular/router";
import {Subscriptions} from "../model/subscriptions.models";
import {CanComponentDeactivate} from "../../guards/can-deactivate.guard";
import {PayComponent} from "./pay/pay.component";
import {OrderDetails} from "../model/OrderDetails";
import {CreditSelectionComponent} from "./credit-selection/credit-selection.component";
import {PlanDetailsComponent} from "./plan-details/plan-details.component";
import {PlanSelectionComponent} from "./plan-selection/plan-selection.component";
import {ResultComponent} from "./result/result.component";
import {TranslatePipe, TranslateService, LangChangeEvent} from "@ngx-translate/core";
import {Subscription} from 'rxjs';
import {APP_CONFIG} from "../../app-config";

// interface Package {
//     name: string;
//     priceMonthly: number | 'free';
//     priceYearly: number | 'free';
//     currency: string;
//     topBadgeInfo: string;
//     planDescription: string;
//     contents: string[];
// }

@Component({
    selector: 'app-payment',
    standalone: true,
    imports: [
        StepperModule,
        InputSwitchModule,
        FormsModule,
        NgIf,
        DividerModule,
        StepsModule,
        PayComponent,
        CreditSelectionComponent,
        PlanDetailsComponent,
        PlanSelectionComponent,
        ResultComponent,
        TranslatePipe,
        UpperCasePipe
    ],
    templateUrl: './payment.component.html',
    styleUrl: './payment.component.scss'
})

export class PaymentComponent implements OnInit, CanComponentDeactivate, OnDestroy {

    appConfig = inject(APP_CONFIG);
    activeStep: number = 0;
    isYearly: boolean = false;
    progressType!: 'subscriptionPayment' | 'creditPayment';
    isAutoRouted: boolean = false;
    selectedPackage!: OrderDetails;
    processState!: 'success' | 'error';
    userSubscription!: Subscriptions;
    selectedCreditAmount: number = 0;
    paymentSteps: MenuItem[] | undefined;

    packages: OrderDetails[] = [];
    private langChangeSub!: Subscription;


    constructor(private subscriptionsService: SubscriptionsService,
                private activatedRoute: ActivatedRoute,
                private router: Router,
                private translateService: TranslateService) {
    }

    ngOnInit() {

        this.langChangeSub = this.translateService.onLangChange.subscribe((event: LangChangeEvent) => {
            this.declarePackages();
            this.buildSteps();
        });

        this.activatedRoute.queryParams.subscribe(params => {
            this.progressType = params['purchaseType'];
            this.isAutoRouted = params['isAutoRouted'] === 'true';
            this.buildSteps();
        })

        this.subscriptionsService.getCurrentUsersSubscriptions().subscribe(res => {
            this.userSubscription = res;
            this.declarePackages();
        });
    }


    private buildSteps() {
        if (this.progressType === 'subscriptionPayment') {
            const basePathSubscription = "sipo.payment-component.payment-steps.subscription";
            this.paymentSteps = [
                {label: this.translateService.instant(`${basePathSubscription}.step1`)},
                {label: this.translateService.instant(`${basePathSubscription}.step2`)},
                {label: this.translateService.instant(`${basePathSubscription}.step3`)},
                {label: this.translateService.instant(`${basePathSubscription}.step4`)}
            ];
        } else {
            const basePathCredit = "sipo.payment-component.payment-steps.credit";
            this.paymentSteps = [
                {label: this.translateService.instant(`${basePathCredit}.step1`)},
                {label: this.translateService.instant(`${basePathCredit}.step2`)},
                {label: this.translateService.instant(`${basePathCredit}.step3`)},
            ];
        }
    }

    declarePackages() {
        const starter = this.createPackage(
            'sipo.packages.starter',
            'free',
            'free',
            false
        );

        const basic = this.createPackage(
            'sipo.packages.basic',
            8.50,
            8.50 * 12 * 0.80,
            this.isYearly
        );

        const business = this.createPackage(
            'sipo.packages.business',
            18.50,
            18.50 * 12 * 0.80,
            this.isYearly
        );

        this.packages =
            this.userSubscription?.plan === 'Starter'
                ? [starter, basic, business]
                : [basic, business];
    }

    private createPackage(
        key: string,
        priceMonthly: number | 'free',
        priceYearly: number | 'free',
        isYearly: boolean
    ): OrderDetails {
        const t = this.translateService;

        let values = {
            isYearly,
            name: t.instant(`${key}.name`),
            priceMonthly,
            priceYearly,
            currency: 'eur',
            topBadgeInfo: t.instant(`${key}.topBadgeInfo`),
            planDescription: t.instant(`${key}.planDescription`),
            planUsageInfo: t.instant(`${key}.planUsageInfo`),
            earlyBird: t.instant(`${key}.earlyBird`),
            contents: this.ensureArray(t.instant(`${key}.contents`)),
        };

        values.contents.forEach((value, index) => {

            values.contents[index] = value.replace("{{appName}}", this.appConfig.domain);

        });


        return values;
    }

    nextStep() {
        if (this.progressType === 'subscriptionPayment') {
            if (this.selectedPackage) {
                this.activeStep++
            }
        } else {
            if (this.selectedCreditAmount && this.selectedCreditAmount > 0) {
                this.activeStep++
            }
        }
    }


    packageSelected($event: OrderDetails) {
        this.selectedPackage = $event;
        this.isYearly = this.selectedPackage.isYearly;
        this.nextStep();
    }

    buySelectedPlan($event: OrderDetails) {
        this.nextStep();
    }

    payCredit(creditAmount: number) {
        this.selectedCreditAmount = creditAmount;
        this.nextStep();
    }

    confirmPayment(state: 'success' | 'error') {
        this.processState = state;

        if (this.progressType === 'subscriptionPayment') {
            this.userSubscription.plan = this.selectedPackage.name;
        }

        if (this.selectedCreditAmount != 0) {
            this.userSubscription.credit += Number(this.selectedCreditAmount);
        }

        if (state === 'success') {
            this.subscriptionsService.updateUserSubscriptions(this.userSubscription).subscribe(res => {
                this.nextStep();
            })
        } else {
            this.nextStep();
        }
    }

    canDeactivate(): boolean {
        const leave = confirm('Möchten Sie die Seite wirklich verlassen? Der gesamte Fortschritt geht verloren!');
        if (!leave) {
            history.pushState({_block_: true}, '', window.location.href);
            this.router.navigateByUrl(this.router.url, {replaceUrl: true}).then(() => {
                console.log('navigate completed')
            })
            console.log(window.history)
        }
        return leave;
    }

    paymentResult(result: any) {
        console.log(result);

        if (result) {
            this.processState = "success";
            this.nextStep();
        } else {
            this.processState = "error";
            this.nextStep();
        }
        //alert('✅ Zahlung erfolgreich!');
    }

    /**
     * Hilfsfunktion: sorgt dafür, dass wir IMMER ein Array zurückbekommen
     */
    private ensureArray(value: any): string[] {
        if (Array.isArray(value)) {
            return value;
        }
        if (typeof value === 'string') {
            try {
                // Falls es ein JSON-String ist wie '["a","b","c"]'
                return JSON.parse(value);
            } catch {
                // Falls nur ein normaler String -> in Array packen
                return [value];
            }
        }
        return [];
    }

    ngOnDestroy() {
        if (this.langChangeSub) {
            this.langChangeSub.unsubscribe();
        }
    }
}
